import React, { useEffect, useMemo, useState } from "react";
import {
  Wallet, Truck, Users as UsersIcon, Receipt, FileText,
  TrendingUp, Plus, Pencil, Trash2, Loader2, X, Check, Download, Send, DollarSign, CalendarClock, ShieldAlert,
} from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { toHandle } from "../utils/handle";

/* ────────────────────────────────────────────────────────────
   Small shared UI atoms used across every tab.
   ──────────────────────────────────────────────────────────── */
function Field({ label, children }) {
  return (
    <div>
      <div className="field-label text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</div>
      {children}
    </div>
  );
}
function Stat({ label, value, color }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 text-lg font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
function EmptyRow({ label }) {
  return <div className="p-8 text-center text-xs text-slate-500">{label}</div>;
}
function ModalShell({ title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="hexa-force-light hexa-form-colorful bg-white rounded-xl shadow-2xl w-[560px] max-w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="acc-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-800">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}
const INPUT = "w-full text-sm px-3 py-2 rounded outline-none";

/* ────────────────────────────────────────────────────────────
   Client Payments — Project No · Project · Client · Total · Received · Balance
   ──────────────────────────────────────────────────────────── */
function ClientPaymentsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/accounts/client-payments"); setRows(data || []); }
    catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => rows.reduce((a, r) => ({
    total: a.total + (r.total_payment || 0),
    received: a.received + (r.received || 0),
    balance: a.balance + (r.balance || 0),
  }), { total: 0, received: 0, balance: 0 }), [rows]);

  const openNew  = () => { setEditing({ project_no: "", project_name: "", client_name: "", client_email: "", total_payment: "", received: "", notes: "" }); setShowForm(true); };
  const openEdit = (r) => { setEditing({ ...r }); setShowForm(true); };
  const save = async () => {
    const payload = { ...editing,
      total_payment: parseFloat(editing.total_payment) || 0,
      received: parseFloat(editing.received) || 0 };
    try {
      if (editing.id) await api.patch(`/accounts/client-payments/${editing.id}`, payload);
      else            await api.post("/accounts/client-payments", payload);
      setShowForm(false); setEditing(null); await load();
    } catch (e) { console.error(e); alert(e?.response?.data?.detail || "Failed to save"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/accounts/client-payments/${id}`); await load(); } catch (e) { console.error(e); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Wallet size={18} className="text-emerald-500" /> Payment Received from Client</h2>
        <button onClick={openNew} data-testid="acc-client-new"
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1 shadow-sm">
          <Plus size={13} /> New Payment
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total Billed"  value={`$${totals.total.toFixed(2)}`}    color="#0ea5e9" />
        <Stat label="Received"      value={`$${totals.received.toFixed(2)}`} color="#22c55e" />
        <Stat label="Outstanding"   value={`$${totals.balance.toFixed(2)}`}  color="#f59e0b" />
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-600">
          <div className="col-span-1">Project No</div>
          <div className="col-span-3">Project Name</div>
          <div className="col-span-2">Client</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-2 text-right">Received</div>
          <div className="col-span-2 text-right">Balance</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? <EmptyRow label="Loading…" /> :
         rows.length === 0 ? <EmptyRow label="No client payments yet." /> :
         rows.map(r => (
          <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-t border-slate-100 text-xs items-center hover:bg-slate-50" data-testid={`acc-client-row-${r.id}`}>
            <div className="col-span-1 font-mono text-slate-500 truncate">{r.project_no || "—"}</div>
            <div className="col-span-3 truncate">{r.project_name}</div>
            <div className="col-span-2 truncate text-slate-600">{r.client_name}</div>
            <div className="col-span-1 text-right font-semibold text-slate-800">${(r.total_payment || 0).toFixed(2)}</div>
            <div className="col-span-2 text-right text-emerald-600 font-semibold">${(r.received || 0).toFixed(2)}</div>
            <div className="col-span-2 text-right font-bold" style={{ color: r.balance > 0 ? "#f59e0b" : "#22c55e" }}>${(r.balance || 0).toFixed(2)}</div>
            <div className="col-span-1 flex items-center justify-end gap-1">
              <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-indigo-600 p-1"><Pencil size={13} /></button>
              <button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      {showForm && editing && (
        <ModalShell
          title={editing.id ? "Edit Client Payment" : "New Client Payment"}
          onClose={() => { setShowForm(false); setEditing(null); }}
          footer={<>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-xs px-3 py-2 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={!editing.project_name || !editing.client_name}
              className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="acc-client-save">
              <Check size={13} /> {editing.id ? "Save" : "Create"}
            </button>
          </>}
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="Project no"><input value={editing.project_no} onChange={(e) => setEditing({...editing, project_no: e.target.value})} className={INPUT} placeholder="P-001" /></Field>
            <Field label="Project name *"><input value={editing.project_name} onChange={(e) => setEditing({...editing, project_name: e.target.value})} className={INPUT} data-testid="acc-client-project" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Client name *"><input value={editing.client_name} onChange={(e) => setEditing({...editing, client_name: e.target.value})} className={INPUT} data-testid="acc-client-name" /></Field>
            <Field label="Client email"><input value={editing.client_email} onChange={(e) => setEditing({...editing, client_email: e.target.value})} className={INPUT} placeholder="billing@client.com" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Total payment"><input type="number" step="0.01" value={editing.total_payment} onChange={(e) => setEditing({...editing, total_payment: e.target.value})} className={INPUT} /></Field>
            <Field label="Received"><input type="number" step="0.01" value={editing.received} onChange={(e) => setEditing({...editing, received: e.target.value})} className={INPUT} /></Field>
          </div>
          <Field label="Notes"><textarea value={editing.notes} onChange={(e) => setEditing({...editing, notes: e.target.value})} rows={3} className={INPUT} /></Field>
        </ModalShell>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Vendor Payments — Project No · Project · Vendor · Total · Paid · Balance
   ──────────────────────────────────────────────────────────── */
function VendorPaymentsTab() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [vp, vs] = await Promise.all([
        api.get("/accounts/vendor-payments"),
        api.get("/vendors").catch(() => ({ data: [] })),
      ]);
      setRows(vp.data || []); setVendors(vs.data || []);
    } catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => rows.reduce((a, r) => ({
    total: a.total + (r.total_payment || 0),
    paid: a.paid + (r.paid || 0),
    balance: a.balance + (r.balance || 0),
  }), { total: 0, paid: 0, balance: 0 }), [rows]);

  const openNew  = () => { setEditing({ project_no: "", project_name: "", vendor_id: "", vendor_name: "", total_payment: "", paid: "", notes: "" }); setShowForm(true); };
  const openEdit = (r) => { setEditing({ ...r }); setShowForm(true); };
  const save = async () => {
    const payload = { ...editing,
      total_payment: parseFloat(editing.total_payment) || 0,
      paid: parseFloat(editing.paid) || 0 };
    if (editing.vendor_id) {
      const v = vendors.find(x => x.id === editing.vendor_id);
      if (v) payload.vendor_name = v.name;
    }
    try {
      if (editing.id) await api.patch(`/accounts/vendor-payments/${editing.id}`, payload);
      else            await api.post("/accounts/vendor-payments", payload);
      setShowForm(false); setEditing(null); await load();
    } catch (e) { console.error(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/accounts/vendor-payments/${id}`); await load(); } catch (e) { console.error(e); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Truck size={18} className="text-amber-500" /> Payment Paid to Vendor</h2>
        <button onClick={openNew} data-testid="acc-vendor-new"
          className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1 shadow-sm">
          <Plus size={13} /> New Payment
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total Payable" value={`$${totals.total.toFixed(2)}`}   color="#0ea5e9" />
        <Stat label="Paid"          value={`$${totals.paid.toFixed(2)}`}    color="#22c55e" />
        <Stat label="Outstanding"   value={`$${totals.balance.toFixed(2)}`} color="#f59e0b" />
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-600">
          <div className="col-span-1">Project No</div>
          <div className="col-span-3">Project Name</div>
          <div className="col-span-2">Vendor</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-2 text-right">Paid</div>
          <div className="col-span-2 text-right">Balance</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? <EmptyRow label="Loading…" /> :
         rows.length === 0 ? <EmptyRow label="No vendor payments yet." /> :
         rows.map(r => (
          <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-t border-slate-100 text-xs items-center hover:bg-slate-50" data-testid={`acc-vendor-row-${r.id}`}>
            <div className="col-span-1 font-mono text-slate-500 truncate">{r.project_no || "—"}</div>
            <div className="col-span-3 truncate">{r.project_name}</div>
            <div className="col-span-2 truncate text-slate-600">{r.vendor_name}</div>
            <div className="col-span-1 text-right font-semibold text-slate-800">${(r.total_payment || 0).toFixed(2)}</div>
            <div className="col-span-2 text-right text-emerald-600 font-semibold">${(r.paid || 0).toFixed(2)}</div>
            <div className="col-span-2 text-right font-bold" style={{ color: r.balance > 0 ? "#f59e0b" : "#22c55e" }}>${(r.balance || 0).toFixed(2)}</div>
            <div className="col-span-1 flex items-center justify-end gap-1">
              <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-indigo-600 p-1"><Pencil size={13} /></button>
              <button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      {showForm && editing && (
        <ModalShell
          title={editing.id ? "Edit Vendor Payment" : "New Vendor Payment"}
          onClose={() => { setShowForm(false); setEditing(null); }}
          footer={<>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-xs px-3 py-2 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={!editing.project_name || !editing.vendor_name}
              className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-50">
              <Check size={13} /> {editing.id ? "Save" : "Create"}
            </button>
          </>}
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="Project no"><input value={editing.project_no} onChange={(e) => setEditing({...editing, project_no: e.target.value})} className={INPUT} placeholder="P-001" /></Field>
            <Field label="Project name *"><input value={editing.project_name} onChange={(e) => setEditing({...editing, project_name: e.target.value})} className={INPUT} /></Field>
          </div>
          <Field label="Vendor">
            <select value={editing.vendor_id} onChange={(e) => setEditing({...editing, vendor_id: e.target.value, vendor_name: vendors.find(v => v.id === e.target.value)?.name || editing.vendor_name})} className={INPUT}>
              <option value="">— Select vendor —</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Field>
          <Field label="Or vendor name *"><input value={editing.vendor_name} onChange={(e) => setEditing({...editing, vendor_name: e.target.value})} className={INPUT} placeholder="Acme Supplies" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Total payment"><input type="number" step="0.01" value={editing.total_payment} onChange={(e) => setEditing({...editing, total_payment: e.target.value})} className={INPUT} /></Field>
            <Field label="Paid"><input type="number" step="0.01" value={editing.paid} onChange={(e) => setEditing({...editing, paid: e.target.value})} className={INPUT} /></Field>
          </div>
          <Field label="Notes"><textarea value={editing.notes} onChange={(e) => setEditing({...editing, notes: e.target.value})} rows={3} className={INPUT} /></Field>
        </ModalShell>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Employee Salary
   ──────────────────────────────────────────────────────────── */
function SalariesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [hrPeople, setHrPeople] = useState([]);
  useEffect(() => { api.get("/hr/employees").then(r => setHrPeople((r.data || []).filter(p => p.has_profile))).catch(() => {}); }, []);
  // Filling from the HR profile also links the slip (by email) so the PDF
  // can print the address, Aadhaar and PAN stored in HR.
  const pickHr = (id) => {
    const p = hrPeople.find(x => x.id === id);
    if (!p) return;
    setEditing(e => ({ ...e,
      employee_name: p.name || e.employee_name,
      employee_email: p.email || e.employee_email,
      employee_code: p.employee_code || e.employee_code,
      designation: p.designation || e.designation,
      department: (p.department || e.department || "").toUpperCase(),
      ctc: e.ctc || (p.monthly_salary ?? ""),
    }));
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/accounts/salaries"); setRows(data || []); }
    catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew  = () => setEditing({ employee_code: "", employee_name: "", employee_email: "", designation: "", department: "", period: new Date().toLocaleString("en-US", { month: "short", year: "numeric" }), working_days: 22, absent_days: 0, ctc: "", pf: "", paid_amount: "", notes: "" }) || setShowForm(true);
  const openEdit = (r) => { setEditing({ ...r }); setShowForm(true); };
  const save = async () => {
    const payload = { ...editing,
      working_days: parseInt(editing.working_days) || 0,
      absent_days: parseInt(editing.absent_days) || 0,
      ctc: parseFloat(editing.ctc) || 0,
      pf: parseFloat(editing.pf) || 0,
      paid_amount: parseFloat(editing.paid_amount) || 0 };
    try {
      if (editing.id) await api.patch(`/accounts/salaries/${editing.id}`, payload);
      else            await api.post("/accounts/salaries", payload);
      setShowForm(false); setEditing(null); await load();
    } catch (e) { console.error(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this salary record?")) return;
    try { await api.delete(`/accounts/salaries/${id}`); await load(); } catch (e) { console.error(e); }
  };
  const download = async (r) => {
    try {
      setBusyId(r.id);
      const res = await api.get(`/accounts/salaries/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `salary-${r.employee_code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    setBusyId("");
  };
  const sendSlip = async (r) => {
    if (!r.employee_email) { alert("No email set on this employee record."); return; }
    try {
      setBusyId(r.id);
      await api.post(`/accounts/salaries/${r.id}/send`);
      alert(`Slip sent to ${r.employee_email}`);
      await load();
    } catch (e) { console.error(e); }
    setBusyId("");
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><UsersIcon size={18} className="text-sky-500" /> Employee Salary</h2>
        <button onClick={openNew} data-testid="acc-salary-new"
          className="bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1 shadow-sm">
          <Plus size={13} /> New Salary Slip
        </button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-x-auto">
        <div className="grid grid-cols-[90px_1.4fr_1fr_100px_70px_60px_80px_70px_90px_110px] px-4 py-2 bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-600 gap-2 min-w-[1000px]">
          <div>Code</div><div>Name</div><div>Designation</div><div>Dept</div>
          <div className="text-right">Days</div><div className="text-right">Abs</div>
          <div className="text-right">CTC</div><div className="text-right">PF</div>
          <div className="text-right">Paid</div><div className="text-right">Salary Slip</div>
        </div>
        {loading ? <EmptyRow label="Loading…" /> :
         rows.length === 0 ? <EmptyRow label="No salary records yet." /> :
         rows.map(r => (
          <div key={r.id} className="grid grid-cols-[90px_1.4fr_1fr_100px_70px_60px_80px_70px_90px_110px] px-4 py-2 border-t border-slate-100 text-xs items-center hover:bg-slate-50 gap-2 min-w-[1000px]" data-testid={`acc-salary-row-${r.id}`}>
            <div className="font-mono text-slate-500">{r.employee_code}</div>
            <div className="truncate">{r.employee_name}<div className="text-[10px] text-slate-500 truncate">{r.period}</div></div>
            <div className="truncate text-slate-600">{r.designation || "—"}</div>
            <div className="truncate text-slate-600">{r.department || "—"}</div>
            <div className="text-right">{r.working_days}</div>
            <div className="text-right text-rose-600">{r.absent_days}</div>
            <div className="text-right font-semibold">₹{(r.ctc || 0).toFixed(0)}</div>
            <div className="text-right text-slate-500">₹{(r.pf || 0).toFixed(0)}</div>
            <div className="text-right font-bold text-emerald-600">₹{(r.paid_amount || 0).toFixed(0)}</div>
            <div className="flex items-center justify-end gap-1">
              <button title="Edit"     onClick={() => openEdit(r)}  className="text-slate-400 hover:text-indigo-600 p-1"><Pencil size={13} /></button>
              <button title="Download" onClick={() => download(r)}  disabled={busyId===r.id} className="text-slate-400 hover:text-sky-600 p-1 disabled:opacity-40" data-testid={`acc-salary-pdf-${r.id}`}><Download size={13} /></button>
              <button title="Send"     onClick={() => sendSlip(r)}  disabled={busyId===r.id} className="text-slate-400 hover:text-emerald-600 p-1 disabled:opacity-40" data-testid={`acc-salary-send-${r.id}`}><Send size={13} /></button>
              <button title="Delete"   onClick={() => del(r.id)}    className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      {showForm && editing && (
        <ModalShell
          title={editing.id ? "Edit Salary Slip" : "New Salary Slip"}
          onClose={() => { setShowForm(false); setEditing(null); }}
          footer={<>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-xs px-3 py-2 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={!editing.employee_code || !editing.employee_name}
              className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-50">
              <Check size={13} /> {editing.id ? "Save" : "Create"}
            </button>
          </>}
        >
          {hrPeople.length > 0 && (
            <Field label="Fill from HR profile">
              <select defaultValue="" onChange={(e) => pickHr(e.target.value)} className={INPUT} data-testid="acc-salary-hr-pick">
                <option value="">Select employee…</option>
                {hrPeople.map(p => <option key={p.id} value={p.id}>{p.name}{p.email ? ` — ${p.email}` : ""}</option>)}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">The slip prints address, Aadhaar and PAN from the HR profile with this email.</div>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee code *"><input value={editing.employee_code} onChange={(e) => setEditing({...editing, employee_code: e.target.value})} className={INPUT} placeholder="EMP-001" /></Field>
            <Field label="Name *"><input value={editing.employee_name} onChange={(e) => setEditing({...editing, employee_name: e.target.value})} className={INPUT} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Designation"><input value={editing.designation} onChange={(e) => setEditing({...editing, designation: e.target.value})} className={INPUT} placeholder="Software Engineer" /></Field>
            <Field label="Department"><select value={editing.department} onChange={(e) => setEditing({...editing, department: e.target.value})} className={INPUT}>
              <option value="">—</option>
              {["SALES","MARKETING","IT","HR","FINANCE","OPERATIONS","OTHER"].map(d => <option key={d} value={d}>{d}</option>)}
            </select></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email"><input value={editing.employee_email} onChange={(e) => setEditing({...editing, employee_email: e.target.value})} className={INPUT} placeholder="emp@company.com" /></Field>
            <Field label="Period"><input value={editing.period} onChange={(e) => setEditing({...editing, period: e.target.value})} className={INPUT} placeholder="Feb 2026" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Working days"><input type="number" value={editing.working_days} onChange={(e) => setEditing({...editing, working_days: e.target.value})} className={INPUT} /></Field>
            <Field label="Absent days"><input type="number" value={editing.absent_days} onChange={(e) => setEditing({...editing, absent_days: e.target.value})} className={INPUT} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="CTC"><input type="number" step="0.01" value={editing.ctc} onChange={(e) => setEditing({...editing, ctc: e.target.value})} className={INPUT} /></Field>
            <Field label="PF"><input type="number" step="0.01" value={editing.pf} onChange={(e) => setEditing({...editing, pf: e.target.value})} className={INPUT} /></Field>
            <Field label="Paid amount"><input type="number" step="0.01" value={editing.paid_amount} onChange={(e) => setEditing({...editing, paid_amount: e.target.value})} className={INPUT} /></Field>
          </div>
          <Field label="Notes"><textarea value={editing.notes} onChange={(e) => setEditing({...editing, notes: e.target.value})} rows={2} className={INPUT} /></Field>
        </ModalShell>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Expenses — mirrors /api/expenses (also exposed in Projects app)
   ──────────────────────────────────────────────────────────── */
function ExpensesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/expenses"); setRows(data || []); }
    catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const paid    = rows.filter(r => r.status === "paid").reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const pending = rows.filter(r => r.status === "pending").reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Receipt size={18} className="text-emerald-500" /> Expenses</h2>
        <div className="text-xs text-slate-500">Manage full expense records from the Projects app.</div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total Spend" value={`$${total.toFixed(2)}`}   color="#0ea5e9" />
        <Stat label="Pending"     value={`$${pending.toFixed(2)}`} color="#f59e0b" />
        <Stat label="Paid"        value={`$${paid.toFixed(2)}`}    color="#22c55e" />
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-600">
          <div className="col-span-2">Date</div>
          <div className="col-span-4">Description</div>
          <div className="col-span-2">Project</div>
          <div className="col-span-2">Vendor</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Amount</div>
        </div>
        {loading ? <EmptyRow label="Loading…" /> :
         rows.length === 0 ? <EmptyRow label="No expenses yet. Add them from Projects → Expenses." /> :
         rows.map(r => (
          <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-t border-slate-100 text-xs items-center hover:bg-slate-50">
            <div className="col-span-2 text-slate-500">{r.date || "—"}</div>
            <div className="col-span-4 truncate">{r.description}</div>
            <div className="col-span-2 truncate text-slate-500">{r.project || "—"}</div>
            <div className="col-span-2 truncate text-slate-500">{r.vendor_name || "—"}</div>
            <div className="col-span-1"><span className="text-[10px] font-semibold uppercase text-slate-600">{r.status}</span></div>
            <div className="col-span-1 text-right font-semibold text-slate-800">{(r.currency || "INR")} {Number(r.amount || 0).toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   GST — Auto invoice per completed project. Edit / PDF / Send.
   ──────────────────────────────────────────────────────────── */
function GstTab() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [gi, pr] = await Promise.all([
        api.get("/accounts/gst-invoices"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setRows(gi.data || []); setProjects(pr.data || []);
    } catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Show only completed projects as suggestions
  const completedProjects = projects.filter(p => ["done", "completed"].includes(p.status));

  const openNew = (proj = null) => {
    setEditing({
      project_id: proj?.id || "",
      project_name: proj?.title || proj?.name || "",
      client_name: proj?.client_name || "",
      client_email: proj?.client_email || "",
      amount: "",
      gst_rate: 18,
      notes: "",
    });
    setShowForm(true);
  };
  const openEdit = (r) => { setEditing({ ...r }); setShowForm(true); };
  const save = async () => {
    const payload = { ...editing, amount: parseFloat(editing.amount) || 0, gst_rate: parseFloat(editing.gst_rate) || 0 };
    try {
      if (editing.id) await api.patch(`/accounts/gst-invoices/${editing.id}`, payload);
      else            await api.post("/accounts/gst-invoices", payload);
      setShowForm(false); setEditing(null); await load();
    } catch (e) { console.error(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await api.delete(`/accounts/gst-invoices/${id}`); await load(); } catch (e) { console.error(e); }
  };
  const download = async (r) => {
    try {
      setBusyId(r.id);
      const res = await api.get(`/accounts/gst-invoices/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${r.invoice_no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    setBusyId("");
  };
  const sendInv = async (r) => {
    if (!r.client_email) { alert("No client email set on this invoice."); return; }
    try {
      setBusyId(r.id);
      const { data } = await api.post(`/accounts/gst-invoices/${r.id}/send`);
      alert(data?.hexa_user ? `Sent inside HEXA to ${data.to}` : `Queued email to ${data.to}`);
      await load();
    } catch (e) { console.error(e); }
    setBusyId("");
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><FileText size={18} className="text-indigo-500" /> GST Tax Invoices</h2>
        <button onClick={() => openNew()} data-testid="acc-gst-new"
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1 shadow-sm">
          <Plus size={13} /> New Invoice
        </button>
      </div>

      {completedProjects.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-semibold mb-2">Completed projects · quick invoice</div>
          <div className="flex flex-wrap gap-2">
            {completedProjects.map(p => (
              <button key={p.id} onClick={() => openNew(p)}
                className="text-xs px-2 py-1 rounded bg-white border border-indigo-300 hover:bg-indigo-100 font-semibold text-indigo-700">
                {p.title || p.name} +
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-600">
          <div className="col-span-2">Invoice #</div>
          <div className="col-span-3">Project</div>
          <div className="col-span-2">Client</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-1 text-right">GST %</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? <EmptyRow label="Loading…" /> :
         rows.length === 0 ? <EmptyRow label="No invoices yet." /> :
         rows.map(r => (
          <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-t border-slate-100 text-xs items-center hover:bg-slate-50" data-testid={`acc-gst-row-${r.id}`}>
            <div className="col-span-2 font-mono font-bold text-slate-800">{r.invoice_no}</div>
            <div className="col-span-3 truncate">{r.project_name}</div>
            <div className="col-span-2 truncate text-slate-600">{r.client_name}</div>
            <div className="col-span-1 text-right font-semibold">${(r.amount || 0).toFixed(2)}</div>
            <div className="col-span-1 text-right text-slate-500">{r.gst_rate}%</div>
            <div className="col-span-1 text-right font-bold text-indigo-600">${(r.total || 0).toFixed(2)}</div>
            <div className="col-span-1">
              <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded" style={{ background: r.status === "sent" ? "#22c55e22" : "#94a3b822", color: r.status === "sent" ? "#16a34a" : "#64748b" }}>{r.status}</span>
            </div>
            <div className="col-span-1 flex items-center justify-end gap-1">
              <button title="Edit"     onClick={() => openEdit(r)} className="text-slate-400 hover:text-indigo-600 p-1"><Pencil size={13} /></button>
              <button title="Download" onClick={() => download(r)} disabled={busyId===r.id} className="text-slate-400 hover:text-sky-600 p-1 disabled:opacity-40" data-testid={`acc-gst-pdf-${r.id}`}><Download size={13} /></button>
              <button title="Send"     onClick={() => sendInv(r)}  disabled={busyId===r.id} className="text-slate-400 hover:text-emerald-600 p-1 disabled:opacity-40" data-testid={`acc-gst-send-${r.id}`}><Send size={13} /></button>
              <button title="Delete"   onClick={() => del(r.id)}   className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      {showForm && editing && (
        <ModalShell
          title={editing.id ? `Edit ${editing.invoice_no || "Invoice"}` : "New Tax Invoice"}
          onClose={() => { setShowForm(false); setEditing(null); }}
          footer={<>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-xs px-3 py-2 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={!editing.project_name || !editing.client_name || !editing.amount}
              className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50">
              <Check size={13} /> {editing.id ? "Save" : "Create"}
            </button>
          </>}
        >
          <Field label="Project *">
            <select value={editing.project_id} onChange={(e) => {
              const p = projects.find(x => x.id === e.target.value);
              setEditing({ ...editing, project_id: e.target.value, project_name: p ? (p.title || p.name) : editing.project_name });
            }} className={INPUT}>
              <option value="">— Select project —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.title || p.name}{["done","completed"].includes(p.status) ? " ✓" : ""}</option>)}
            </select>
          </Field>
          <Field label="Or project name *"><input value={editing.project_name} onChange={(e) => setEditing({...editing, project_name: e.target.value})} className={INPUT} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Client name *"><input value={editing.client_name} onChange={(e) => setEditing({...editing, client_name: e.target.value})} className={INPUT} /></Field>
            <Field label="Client email"><input value={editing.client_email} onChange={(e) => setEditing({...editing, client_email: e.target.value})} className={INPUT} placeholder="client@company.com" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount *"><input type="number" step="0.01" value={editing.amount} onChange={(e) => setEditing({...editing, amount: e.target.value})} className={INPUT} /></Field>
            <Field label="GST %"><input type="number" step="0.1" value={editing.gst_rate} onChange={(e) => setEditing({...editing, gst_rate: e.target.value})} className={INPUT} /></Field>
          </div>
          <Field label="Notes"><textarea value={editing.notes} onChange={(e) => setEditing({...editing, notes: e.target.value})} rows={2} className={INPUT} /></Field>
        </ModalShell>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Profit & Loss — project × time period
   ──────────────────────────────────────────────────────────── */
function PnLTab() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/accounts/pnl?period=${period}`); setData(data); }
    catch { setData(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [period]);

  const totals = data?.totals || {};
  const totalIncome  = Object.values(totals).reduce((s, b) => s + b.income, 0);
  const totalExpense = Object.values(totals).reduce((s, b) => s + b.expense, 0);
  const totalNet     = totalIncome - totalExpense;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><TrendingUp size={18} className="text-emerald-500" /> Profit &amp; Loss</h2>
        <div className="flex items-center gap-1 border border-slate-200 rounded overflow-hidden bg-white">
          {[
            { k: "month",   label: "Monthly" },
            { k: "quarter", label: "Quarterly" },
            { k: "half",    label: "Half-yearly" },
            { k: "year",    label: "Yearly" },
          ].map(p => (
            <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`acc-pnl-${p.k}`}
              className={`text-xs font-semibold px-3 py-1.5 transition ${period === p.k ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total Income"  value={`$${totalIncome.toFixed(2)}`}                             color="#22c55e" />
        <Stat label="Total Expense" value={`$${totalExpense.toFixed(2)}`}                            color="#ef4444" />
        <Stat label="Net"           value={`${totalNet >= 0 ? "+" : "-"}$${Math.abs(totalNet).toFixed(2)}`} color={totalNet >= 0 ? "#22c55e" : "#ef4444"} />
      </div>

      {loading ? <EmptyRow label="Loading…" /> :
       !data || !data.projects.length ? <EmptyRow label="No P&L data yet. Add client payments, vendor payments, salaries or expenses." /> :
       <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="grid px-4 py-2 bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-600" style={{ gridTemplateColumns: `220px repeat(${data.buckets.length}, minmax(140px, 1fr)) 120px` }}>
            <div>Project</div>
            {data.buckets.map(b => <div key={b} className="text-right">{b}</div>)}
            <div className="text-right">Net</div>
          </div>
          {data.projects.map(p => {
            const projNet = data.buckets.reduce((s, b) => s + ((p.buckets[b]?.net) || 0), 0);
            return (
              <div key={p.project} className="grid px-4 py-2 border-t border-slate-100 text-xs items-center hover:bg-slate-50" style={{ gridTemplateColumns: `220px repeat(${data.buckets.length}, minmax(140px, 1fr)) 120px` }}>
                <div className="font-semibold truncate">{p.project.replace(/^__(.*)__$/, "$1")}</div>
                {data.buckets.map(b => {
                  const c = p.buckets[b] || { income: 0, expense: 0, net: 0 };
                  return (
                    <div key={b} className="text-right">
                      <div className="text-emerald-600">+${c.income.toFixed(0)}</div>
                      <div className="text-rose-500">-${c.expense.toFixed(0)}</div>
                      <div className={`font-bold ${c.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{c.net >= 0 ? "+" : "-"}${Math.abs(c.net).toFixed(0)}</div>
                    </div>
                  );
                })}
                <div className={`text-right font-bold ${projNet >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{projNet >= 0 ? "+" : "-"}${Math.abs(projNet).toFixed(2)}</div>
              </div>
            );
          })}
          {/* Bucket totals row */}
          <div className="grid px-4 py-2 border-t-2 border-slate-300 bg-slate-50 text-xs items-center font-bold" style={{ gridTemplateColumns: `220px repeat(${data.buckets.length}, minmax(140px, 1fr)) 120px` }}>
            <div className="text-slate-700">TOTALS</div>
            {data.buckets.map(b => {
              const t = totals[b] || { income: 0, expense: 0, net: 0 };
              return (
                <div key={b} className="text-right">
                  <div className="text-emerald-600">+${t.income.toFixed(0)}</div>
                  <div className="text-rose-500">-${t.expense.toFixed(0)}</div>
                  <div className={`${t.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{t.net >= 0 ? "+" : "-"}${Math.abs(t.net).toFixed(0)}</div>
                </div>
              );
            })}
            <div className={`text-right ${totalNet >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{totalNet >= 0 ? "+" : "-"}${Math.abs(totalNet).toFixed(2)}</div>
          </div>
        </div>
       </div>}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Root Accounts app — 6 tabs
   ──────────────────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────
   User Payments — every HEXA user treated as a client: plan,
   15-day trial window, expiry & manual extension (super admin only)
   ──────────────────────────────────────────────────────────── */
function UserPaymentsTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extending, setExtending] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } catch (e) { console.warn(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const statusOf = (u) => {
    if (u.is_super_admin) return { label: "Super Admin", color: "#8b5cf6" };
    if (!u.subscription_expires) return { label: "No trial set", color: "#94a3b8" };
    const expired = new Date(u.subscription_expires) < new Date();
    return expired ? { label: "Expired", color: "#ef4444" } : { label: "Active trial", color: "#10b981" };
  };

  const extend = async (u, days) => {
    setExtending(u.id);
    try {
      const base = u.subscription_expires ? new Date(u.subscription_expires) : new Date();
      base.setDate(base.getDate() + days);
      await api.patch(`/admin/users/${u.id}`, { subscription_expires: base.toISOString() });
      await load();
    } catch (e) { console.warn(e); }
    setExtending(null);
  };

  const activeCount = users.filter((u) => !u.is_super_admin && u.subscription_expires && new Date(u.subscription_expires) >= new Date()).length;
  const expiredCount = users.filter((u) => !u.is_super_admin && u.subscription_expires && new Date(u.subscription_expires) < new Date()).length;

  return (
    <div className="p-5 space-y-4" data-testid="acc-user-payments-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-800">User Payments</div>
          <div className="text-[11px] text-slate-500">Every HEXA user is a client — 15-day free trial, extendable by super admin only.</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total Users" value={users.length} color="#0ea5e9" />
        <Stat label="Active Trials" value={activeCount} color="#10b981" />
        <Stat label="Expired" value={expiredCount} color="#ef4444" />
      </div>
      {loading ? (
        <div className="p-8 text-center"><Loader2 className="animate-spin inline text-slate-400" /></div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">User</th>
                <th className="px-3 py-2 text-left">Plan</th>
                <th className="px-3 py-2 text-left">Trial Start</th>
                <th className="px-3 py-2 text-left">Expires</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Extend</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const st = statusOf(u);
                return (
                  <tr key={u.id} data-testid={`acc-user-payment-row-${u.id}`} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-800">{u.name}</div>
                      <div className="text-slate-400 text-[10px]">{u.handle || toHandle(u.address)}</div>
                    </td>
                    <td className="px-3 py-2 capitalize">{u.plan}</td>
                    <td className="px-3 py-2">{u.subscription_started ? new Date(u.subscription_started).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-2">{u.subscription_expires ? new Date(u.subscription_expires).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full text-white text-[10px] font-semibold" style={{ background: st.color }}>{st.label}</span></td>
                    <td className="px-3 py-2">
                      {!u.is_super_admin && (
                        <div className="flex gap-1">
                          <button data-testid={`acc-extend-15-${u.id}`} disabled={extending === u.id} onClick={() => extend(u, 15)} className="text-[10px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 flex items-center gap-1"><CalendarClock size={10} /> +15d</button>
                          <button data-testid={`acc-extend-30-${u.id}`} disabled={extending === u.id} onClick={() => extend(u, 30)} className="text-[10px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50">+30d</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { k: "client",  label: "Client Payments", icon: Wallet,     color: "#10b981" },
  { k: "vendor",  label: "Vendor Payments", icon: Truck,      color: "#f59e0b" },
  { k: "salary",  label: "Employee Salary", icon: UsersIcon,  color: "#0ea5e9" },
  { k: "expense", label: "Expenses",        icon: Receipt,    color: "#8b5cf6" },
  { k: "gst",     label: "GST Invoices",    icon: FileText,   color: "#6366f1" },
  { k: "pnl",     label: "Profit & Loss",   icon: TrendingUp, color: "#22c55e" },
];

export default function Accounts() {
  const { user } = useAuth();
  const isSuper = !!user?.is_super_admin;
  const [tab, setTab] = useState(isSuper ? "userpay" : "client");
  const tabs = isSuper
    ? [{ k: "userpay", label: "User Payments", icon: ShieldAlert, color: "#8b5cf6" }, ...TABS]
    : TABS;
  return (
    <div className="h-full flex bg-slate-50">
      <aside className="w-56 shrink-0 bg-white border-r border-slate-200 py-4 flex flex-col">
        <div className="px-4 pb-3 flex items-center gap-2">
          <DollarSign size={18} className="text-emerald-500" />
          <div>
            <div className="text-sm font-bold text-slate-800">Accounts</div>
            <div className="text-[10px] text-slate-500">Finance suite</div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2 space-y-0.5">
          {tabs.map(t => {
            const Icon = t.icon; const active = tab === t.k;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`acc-tab-${t.k}`}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded text-xs font-semibold transition ${active ? "hexa-tab-active shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
                >
                <Icon size={13} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-y-auto">
        {tab === "userpay" && isSuper && <UserPaymentsTab />}
        {tab === "client"  && <ClientPaymentsTab />}
        {tab === "vendor"  && <VendorPaymentsTab />}
        {tab === "salary"  && <SalariesTab />}
        {tab === "expense" && <ExpensesTab />}
        {tab === "gst"     && <GstTab />}
        {tab === "pnl"     && <PnLTab />}
      </main>
    </div>
  );
}
